import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import FluentChat12Regular from '~icons/fluent/chat-12-regular'
import FluentSparkle20Filled from '~icons/fluent/sparkle-20-filled'
import IcOutlineCollections from '~icons/ic/outline-collections'
import IcOutlineMenuBook from '~icons/ic/outline-menu-book'
import type { Artwork, Manga } from 'src/services/apollo/generated/graphql'
import { ArtworkType, MangaType } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'

/**
 * Artwork Type Label
 *
 * To properly show images count for albums, the `chapter` field is required.
 */
export default function ArtworkTypeLabel(
  props: StyleProps &
    (
      | { artwork: Pick<Artwork, 'type' | 'extra'> }
      | { manga: Manga }
      | {
          type:
            | typeof ArtworkType.Album
            | typeof ArtworkType.Comic
            | 'ANIMATED'
            | 'ANIMATED_COMIC'
            | undefined
        }
    ),
) {
  const { t } = useTranslation(['artwork'])

  const type = match(props)
    .returnType<{
      icon: ReactNode
      label: string
      className: string
    } | null>()
    .with(
      { type: ArtworkType.Comic },
      { artwork: { type: ArtworkType.Comic } },
      { manga: { type: MangaType.Comic } },
      () => ({
        icon: <IcOutlineMenuBook className="w-5 h-5 -translate-y-px" />,
        label: 'Comic',
        className: 'bg-indigo-700',
      }),
    )
    .with({ manga: { type: MangaType.Album } }, args => ({
      icon: (
        <IcOutlineCollections className="w-5 h-5" style={{ marginLeft: -1 }} />
      ),
      label:
        args?.manga?.chapter?.content?.mediaIds?.length?.toString() ?? 'Album',
      className: 'bg-indigo-700',
    }))
    .with(
      { type: 'ANIMATED_COMIC' },
      { artwork: { type: ArtworkType.AnimatedComic } },
      { manga: { type: MangaType.AnimatedComic } },
      () => ({
        icon: (
          <FluentSparkle20Filled
            className="w-5 h-5"
            style={{ marginLeft: -3 }}
          />
        ),
        label: 'Animated Comic',
        className: 'bg-indigo-700',
      }),
    )
    .with(
      { type: 'ANIMATED' },
      {
        artwork: { type: ArtworkType.AnimatedArtwork },
      },
      () => ({
        icon: (
          <FluentSparkle20Filled
            className="w-5 h-5"
            style={{ marginLeft: -3 }}
          />
        ),
        label: t('artwork:type.animated', { context: 'short' }),
        className: 'bg-purple-700',
      }),
    )
    .with({ artwork: { extra: { taskProcessType: 'chat' } } }, () => ({
      icon: (
        <FluentChat12Regular className="w-5 h-5" style={{ marginLeft: -3 }} />
      ),
      label: t(`artwork:type.chat-edit`),
      className: 'bg-purple-700',
    }))
    .otherwise(() => null)

  return (
    type && (
      <div
        className={cx(
          'px-2 leading-6 text-sm whitespace-nowrap flex items-center gap-1 text-white font-medium rounded',
          type.className,
          props.className,
        )}
        style={props.style}
      >
        {type.icon}
        {type.label}
      </div>
    )
  )
}
